import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { listar, COLECOES } from '../../services/firestore';
import {
  calcularStatusEstoque,
  posicaoDeveAparecer,
  STATUS,
} from '../../utils/estoque';
import styles from './Dashboard.module.css';

// ============================================================
// Helpers locais
// ============================================================

function getSaudacao() {
  const hora = new Date().getHours();
  if (hora < 12) return 'Bom dia';
  if (hora < 18) return 'Boa tarde';
  return 'Boa noite';
}

function getDataExtenso() {
  const d = new Date();
  return d.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function calcularRealizadoPorProduto(os, movimentacoes) {
  const movsDaOS = movimentacoes.filter(
    (m) =>
      m.status === 'EFETIVADA' &&
      (m.tipo === 'SAIDA' || m.tipo === 'DEVOLUCAO') &&
      (m.os || '').trim() === (os.numero || '').trim()
  );
  const realPorProduto = {};
  movsDaOS.forEach((mov) => {
    (mov.itens || []).forEach((it) => {
      const sinal = mov.tipo === 'SAIDA' ? +1 : -1;
      realPorProduto[it.produtoId] =
        (realPorProduto[it.produtoId] || 0) + sinal * (it.qtd || 0);
    });
  });
  return realPorProduto;
}

function osTemEstouro(os, movimentacoes) {
  const real = calcularRealizadoPorProduto(os, movimentacoes);
  return (os.materiais || []).some(
    (m) => m.previsto && m.previsto > 0 && (real[m.produtoId] || 0) > m.previsto
  );
}

const badgeClass = {
  Aberta: 'bOk',
  'Em execução': 'bExecucao',
  Concluída: 'bConcluida',
  Estouro: 'bEstouro',
  Pausada: 'bPausada',
};

// ============================================================
// COMPONENTE
// ============================================================
export default function Dashboard() {
  const navigate = useNavigate();
  const usuario = useAuthStore((s) => s.usuario);

  const [aberto, setAberto] = useState({ os: false, est: false });
  const toggle = (id) => setAberto((prev) => ({ ...prev, [id]: !prev[id] }));

  const [produtos, setProdutos] = useState([]);
  const [posicoes, setPosicoes] = useState([]);
  const [estoques, setEstoques] = useState([]);
  const [ordensServico, setOrdensServico] = useState([]);
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const [prods, pos, ests, oss, movs] = await Promise.all([
        listar(COLECOES.PRODUTOS),
        listar(COLECOES.ESTOQUE_POSICAO),
        listar(COLECOES.ESTOQUES),
        listar(COLECOES.ORDENS_SERVICO),
        listar(COLECOES.MOVIMENTACOES),
      ]);
      setProdutos(prods.filter((p) => p.ativo !== false));
      setPosicoes(pos);
      setEstoques(ests.filter((e) => e.ativo !== false));
      setOrdensServico(oss);
      setMovimentacoes(movs);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar dashboard.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // ============================================================
  // OS ATIVAS (Aberta + Em execução)
  // ============================================================
  const osAtivas = useMemo(() => {
    return ordensServico
      .filter((o) => o.status === 'Aberta' || o.status === 'Em execução')
      .map((o) => ({
        ...o,
        temEstouro: osTemEstouro(o, movimentacoes),
      }))
      .sort((a, b) => (b.numero || '').localeCompare(a.numero || ''));
  }, [ordensServico, movimentacoes]);

  // ============================================================
  // ESTOQUE EM ATENÇÃO (CRITICO + ZERADO + ATENCAO)
  // ============================================================
  const estoqueAtencao = useMemo(() => {
    const mapProdutos = {};
    produtos.forEach((p) => {
      mapProdutos[p.id] = p;
    });

    const itens = [];
    posicoes.forEach((pos) => {
      const prod = mapProdutos[pos.produtoId];
      if (!prod) return;

      const estoqueDoc = estoques.find((e) => e.id === pos.estoqueId);
      const nivel = (estoqueDoc?.niveis || []).find(
        (n) => n.produtoId === pos.produtoId
      );
      const min = Number(nivel?.minimo) || 0;
      const max = Number(nivel?.maximo) || 0;
      const qtd = Number(pos.qtdReal) || 0;

      if (!posicaoDeveAparecer(qtd, min)) return;
      if (min <= 0) return;

      const statusKey = calcularStatusEstoque(qtd, min, max);

      if (
        statusKey === STATUS.CRITICO ||
        statusKey === STATUS.ZERADO ||
        statusKey === STATUS.ATENCAO
      ) {
        itens.push({
          cor: prod.cor,
          resina: prod.resina,
          unidade: prod.unidade,
          estoque: pos.estoqueNome || estoqueDoc?.nome || '',
          qtd,
          min,
          statusKey,
        });
      }
    });

    return itens.sort((a, b) => {
      const ordem = { ZERADO: 0, CRITICO: 1, ATENCAO: 2 };
      return (ordem[a.statusKey] ?? 9) - (ordem[b.statusKey] ?? 9);
    });
  }, [produtos, posicoes, estoques]);

  // ============================================================
  // ALERTAS (topo) — montado a partir dos blocos acima
  // ============================================================
  const alertas = useMemo(() => {
    const lista = [];

    const osComEstouro = osAtivas.filter((o) => o.temEstouro);
    osComEstouro.slice(0, 2).forEach((o) => {
      lista.push({
        tipo: 'critico',
        title: `${o.numero} — ${o.clienteNome || 'sem cliente'} com estouro de consumo`,
        sub: 'Há produtos com realizado acima do previsto. Verifique a OS.',
        rota: '/os',
      });
    });

    const criticos = estoqueAtencao.filter(
      (e) => e.statusKey === STATUS.CRITICO || e.statusKey === STATUS.ZERADO
    );
    if (criticos.length > 0) {
      const primeiro = criticos[0];
      const extras = criticos.length - 1;
      lista.push({
        tipo: 'warn',
        title:
          extras > 0
            ? `${primeiro.cor}${primeiro.resina ? ' · ' + primeiro.resina : ''} (${primeiro.estoque}) — ${primeiro.qtd} ${primeiro.unidade || 'un'}. (mín: ${primeiro.min}) · e mais ${extras}`
            : `${primeiro.cor}${primeiro.resina ? ' · ' + primeiro.resina : ''} (${primeiro.estoque}) abaixo do mínimo — ${primeiro.qtd} ${primeiro.unidade || 'un'}. (mín: ${primeiro.min})`,
        sub: 'Repor urgente',
        rota: '/relatorios/minimo',
      });
    }

    return lista;
  }, [osAtivas, estoqueAtencao]);

  // ============================================================
  // SAUDAÇÃO E NOME
  // ============================================================
  const primeiroNome = useMemo(() => {
    if (usuario?.displayName) return usuario.displayName.split(' ')[0];
    if (usuario?.email) {
      const local = usuario.email.split('@')[0];
      const parte = local.split('.')[0];
      return parte.charAt(0).toUpperCase() + parte.slice(1);
    }
    return 'Usuário';
  }, [usuario]);

  const dataHoje = useMemo(() => getDataExtenso(), []);
  const saudacao = useMemo(() => getSaudacao(), []);

  const ESTOQUE_LIMITE = 5;

  return (
    <>
      {/* TOPBAR — saudação + botão Ler QR */}
      <div className={styles.topbar}>
        <div>
          <div className={styles.pageTitle}>
            {saudacao}, {primeiroNome} 👋
          </div>
          <div className={styles.pageSub}>
            {dataHoje.charAt(0).toUpperCase() + dataHoje.slice(1)} ·{' '}
            {carregando ? '...' : `${osAtivas.length} OS ativas`}
          </div>
        </div>
      </div>

      {/* Erro */}
      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '0 0 14px',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      {/* ALERTAS */}
      {!carregando && alertas.length > 0 && (
        <div className={styles.alertas}>
          {alertas.map((a, i) => (
            <div
              key={i}
              className={`${styles.alerta} ${styles['alerta_' + a.tipo]}`}
              onClick={() => navigate(a.rota)}
            >
              <div className={`${styles.alertaDot} ${styles['ad_' + a.tipo]}`} />
              <div className={styles.alertaBody}>
                <div className={styles.alertaTitle}>{a.title}</div>
                <div className={styles.alertaSub}>{a.sub}</div>
              </div>
              <span className={styles.alertaArrow}>›</span>
            </div>
          ))}
        </div>
      )}

      {!carregando && alertas.length === 0 && (
        <div
          style={{
            background: '#E1F5EE',
            color: '#0F6E56',
            border: '1px solid #B8E5D3',
            borderRadius: 10,
            padding: '14px 18px',
            margin: '0 0 14px',
            fontSize: 13,
          }}
        >
          ✓ Tudo em ordem. Nenhum alerta no momento.
        </div>
      )}

      {/* CARDS COLAPSÁVEIS */}
      <div className={styles.grid3}>
        {/* OS ativas */}
        <div className={`${styles.card} ${aberto.os ? styles.open : ''}`}>
          <div className={styles.cardHead} onClick={() => toggle('os')}>
            <div className={styles.cardHeadLeft}>
              <span className={styles.cardDot} style={{ background: '#0F6E56' }} />
              <span className={styles.cardTitle}>OS ativas</span>
            </div>
            <div className={styles.cardHeadRight}>
              <span className={styles.cardCount} style={{ background: '#E1F5EE', color: '#0F6E56' }}>
                {carregando ? '...' : osAtivas.length}
              </span>
              <span className={styles.cardArrow}>▾</span>
            </div>
          </div>
          <div className={styles.cardBody}>
            {carregando ? (
              <div style={{ padding: 12, color: '#888', fontSize: 12 }}>Carregando...</div>
            ) : osAtivas.length === 0 ? (
              <div style={{ padding: 12, color: '#888', fontSize: 12 }}>
                Nenhuma OS ativa no momento.
              </div>
            ) : (
              osAtivas.slice(0, 5).map((os) => {
                const statusExibido = os.temEstouro ? 'Estouro' : os.status;
                return (
                  <div
                    key={os.id}
                    className={styles.row}
                    onClick={() => navigate('/os')}
                  >
                    <span className={styles.osId}>{os.numero}</span>
                    <span className={styles.osCli}>{os.clienteNome || '—'}</span>
                    <span
                      className={`${styles.badge} ${styles[badgeClass[statusExibido]] || ''}`}
                    >
                      {statusExibido}
                    </span>
                  </div>
                );
              })
            )}
            <span className={styles.verMais} onClick={() => navigate('/os')}>
              Ver todas →
            </span>
          </div>
        </div>

        {/* Atenção no estoque */}
        <div className={`${styles.card} ${aberto.est ? styles.open : ''}`}>
          <div className={styles.cardHead} onClick={() => toggle('est')}>
            <div className={styles.cardHeadLeft}>
              <span className={styles.cardDot} style={{ background: '#B51013' }} />
              <span className={styles.cardTitle}>Atenção no estoque</span>
            </div>
            <div className={styles.cardHeadRight}>
              <span className={styles.cardCount} style={{ background: '#FCEBEB', color: '#A32D2D' }}>
                {carregando ? '...' : `${estoqueAtencao.length} ${estoqueAtencao.length === 1 ? 'alerta' : 'alertas'}`}
              </span>
              <span className={styles.cardArrow}>▾</span>
            </div>
          </div>
          <div className={styles.cardBody}>
            {carregando ? (
              <div style={{ padding: 12, color: '#888', fontSize: 12 }}>Carregando...</div>
            ) : estoqueAtencao.length === 0 ? (
              <div style={{ padding: 12, color: '#888', fontSize: 12 }}>
                Nenhuma posição em atenção no momento.
              </div>
            ) : (
              estoqueAtencao.slice(0, ESTOQUE_LIMITE).map((p, idx) => {
                const ehCritico =
                  p.statusKey === STATUS.CRITICO || p.statusKey === STATUS.ZERADO;
                return (
                  <div
                    key={idx}
                    className={styles.row}
                    onClick={() => navigate('/relatorios/minimo')}
                  >
                    <div className={styles.prodMain}>
                      <div className={styles.prodName}>
                        {p.cor || '—'}
                      </div>
                      <div className={styles.prodMeta}>
                        {p.resina ? `${p.resina} · ` : ''}
                        {p.estoque}: {p.qtd} {p.unidade || 'un'} (mín {p.min})
                      </div>
                    </div>
                    <span
                      className={`${styles.prodTag} ${
                        ehCritico ? styles.tagVermelho : styles.tagAmarelo
                      }`}
                    >
                      {p.statusKey === STATUS.ZERADO
                        ? 'Zerado'
                        : p.statusKey === STATUS.CRITICO
                        ? 'Abaixo mín.'
                        : 'Atenção'}
                    </span>
                  </div>
                );
              })
            )}
            <span className={styles.verMais} onClick={() => navigate('/relatorios/minimo')}>
              {estoqueAtencao.length > ESTOQUE_LIMITE
                ? `+${estoqueAtencao.length - ESTOQUE_LIMITE} no relatório →`
                : 'Ver relatório completo →'}
            </span>
          </div>
        </div>

      </div>
    </>
  );
}
